import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import MarketingAssetModal from "@/components/MarketingAssetModal";
import {
  Plus, Trash2, Save, X, ExternalLink, Eye, Download, Search,
  FileText, Mail, Image as ImageIcon, Megaphone, Layers, Pencil,
} from "lucide-react";

const KINDS = ["template", "graphic", "copy", "email", "deck"];
const STATUSES = ["active", "draft", "archived"];
const PROGRAM_CATS = ["sleep", "metabolic", "hormone", "diagnostics", "wellness"];
const TEMPLATE_KINDS = ["", "landing_page", "email_sequence", "social_kit", "pitch_deck", "intake_form", "one_pager"];
const EMPTY = {
  title: "", kind: "template", category: "", description: "", download_url: "",
  program_categories: [], template_kind: "", template_body: "", status: "active",
};

const KIND_TINT = {
  template: "bg-indigo-50 text-indigo-700 border-indigo-200",
  graphic: "bg-amber-50 text-amber-800 border-amber-200",
  copy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  email: "bg-blue-50 text-blue-700 border-blue-200",
  deck: "bg-violet-50 text-violet-700 border-violet-200",
};
const KIND_ICON = {
  template: Layers, graphic: ImageIcon, copy: Megaphone, email: Mail, deck: FileText,
};
const CATEGORY_TINT = {
  sleep: "bg-indigo-50 text-indigo-700",
  metabolic: "bg-amber-50 text-amber-800",
  hormone: "bg-rose-50 text-rose-700",
  diagnostics: "bg-blue-50 text-blue-700",
  wellness: "bg-emerald-50 text-emerald-700",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMarketing() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [previewing, setPreviewing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState("all");
  const [filterCat, setFilterCat] = useState("all");

  const load = async () => {
    try { const r = await api.get("/marketing-assets"); setItems(r.data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const togglePc = (cat) => setForm((f) => ({
    ...f,
    program_categories: f.program_categories.includes(cat)
      ? f.program_categories.filter((x) => x !== cat)
      : [...f.program_categories, cat],
  }));

  const save = async () => {
    try {
      if (editing === "new") await api.post("/marketing-assets", form);
      else await api.patch(`/marketing-assets/${editing}`, form);
      toast.success("Saved.");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/marketing-assets/${id}`); load(); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startNew = (kind) => {
    setEditing("new");
    setForm({ ...EMPTY, kind: kind || EMPTY.kind });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const downloadDefaultPdf = async (a) => {
    try {
      const tok = sessionStorage.getItem("nodera_token") || localStorage.getItem("nodera_token");
      const r = await fetch(`${BACKEND_URL}/api/marketing-assets/${a.id}/preview`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!r.ok) throw new Error("preview unavailable");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const isMobile = /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");
      if (isMobile) {
        // `download` attribute is silently ignored on iOS Safari + most Android in-app
        // browsers for blob URLs. Open in a new tab so the OS share sheet can save.
        const win = window.open(url, "_blank");
        if (!win) window.location.href = url;
        toast.success("PDF opened. Tap the share icon to save it to Files.");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${a.title || "asset"}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((a) => {
    if (filterKind !== "all" && a.kind !== filterKind) return false;
    if (filterCat !== "all" && !(a.program_categories || []).includes(filterCat)) return false;
    if (search.trim() && !(a.title || "").toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  // Group by kind for visual sectioning.
  const byKind = filterKind === "all"
    ? KINDS.reduce((acc, k) => { const arr = filtered.filter((a) => a.kind === k); if (arr.length) acc[k] = arr; return acc; }, {})
    : { [filterKind]: filtered };

  return (
    <ToolPageShell
      eyebrow="Admin · Catalog"
      title="Marketing assets"
      intro="Templates, decks, copy, and graphics distributed to approved clinics through the portal. Upload, edit, and preview the same co-branded PDF that clinics see."
      actions={
        <button
          onClick={() => startNew()}
          data-testid="marketing-new-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> New asset
        </button>
      }
    >
      {editing && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4" data-testid="marketing-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title" required>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="marketing-form-title" />
            </Field>
            <Field label="Kind">
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls} data-testid="marketing-form-kind">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-y`} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Template kind" hint="Drives the PDF preview layout">
              <select value={form.template_kind || ""} onChange={(e) => setForm({ ...form, template_kind: e.target.value })} className={inputCls}>
                {TEMPLATE_KINDS.map((k) => <option key={k} value={k}>{k || "—"}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Template body" hint="Use {{clinic_name}} {{clinic_email}} {{clinic_contact}} {{first_name}} placeholders. Rendered in PDF preview.">
            <textarea
              rows={8}
              value={form.template_body || ""}
              onChange={(e) => setForm({ ...form, template_body: e.target.value })}
              className={`${inputCls} resize-y font-mono text-xs`}
              data-testid="marketing-form-template-body"
              placeholder="Welcome to {{clinic_name}}'s ..."
            />
          </Field>
          <Field label="Download URL (optional)">
            <input value={form.download_url || ""} onChange={(e) => setForm({ ...form, download_url: e.target.value })} className={inputCls} placeholder="https://" />
          </Field>
          <Field label="Program categories">
            <div className="flex flex-wrap gap-2">
              {PROGRAM_CATS.map((c) => {
                const active = form.program_categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => togglePc(c)}
                    className={`text-xs px-3 py-1.5 rounded-full border capitalize ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="flex items-center gap-2 pt-2">
            <button onClick={save} data-testid="marketing-save" className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button onClick={() => { setEditing(null); setForm(EMPTY); }} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="marketing-filters">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mr-1">Filter</div>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            data-testid="marketing-search"
            className="w-full rounded-full border border-slate-200 pl-8 pr-3 py-1.5 text-xs focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
          />
        </div>
        <button
          onClick={() => setFilterKind("all")}
          data-testid="marketing-kind-all"
          className={`text-xs px-3 py-1.5 rounded-full border ${filterKind === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
        >
          All kinds
        </button>
        {KINDS.map((k) => (
          <button
            key={k}
            onClick={() => setFilterKind(k)}
            data-testid={`marketing-kind-${k}`}
            className={`text-xs capitalize px-3 py-1.5 rounded-full border ${filterKind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
          >
            {k}
          </button>
        ))}
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="marketing-cat-filter"
          className="text-xs border border-slate-200 rounded-full px-2.5 py-1.5 bg-white"
        >
          <option value="all">All categories</option>
          {PROGRAM_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {filterKind !== "all" && (
          <button
            onClick={() => startNew(filterKind)}
            data-testid={`marketing-add-in-${filterKind}`}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-3 py-1.5 capitalize"
          >
            <Plus className="w-3 h-3" /> Add {filterKind}
          </button>
        )}
      </div>

      {/* Sectioned cards by kind */}
      {Object.keys(byKind).length === 0 ? (
        <div className="text-center text-slate-500 py-12 rounded-2xl border border-dashed border-slate-300 bg-white">
          {items.length === 0 ? "No assets yet. Click \"New asset\" to add one." : "No assets match the current filters."}
        </div>
      ) : (
        Object.entries(byKind).map(([kind, list]) => {
          const Icon = KIND_ICON[kind] || FileText;
          return (
            <div key={kind} className="mb-6" data-testid={`marketing-section-${kind}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-700" />
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-700 capitalize">
                    {kind}s
                  </div>
                  <span className="text-[10px] text-slate-400">· {list.length}</span>
                </div>
                <button
                  onClick={() => startNew(kind)}
                  data-testid={`marketing-section-add-${kind}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                >
                  <Plus className="w-3 h-3" /> Add {kind}
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((a) => (
                  <AssetCard
                    key={a.id}
                    a={a}
                    onPreview={() => setPreviewing(a)}
                    onDownload={() => downloadDefaultPdf(a)}
                    onEdit={() => { setEditing(a.id); setForm({ ...EMPTY, ...a, program_categories: a.program_categories || [] }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    onDelete={() => remove(a.id)}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}

      {previewing && (
        <MarketingAssetModal
          asset={previewing}
          clinic={{ clinic_name: "Sample Clinic", email: "front-desk@sample.example", name: "Sample" }}
          onClose={() => setPreviewing(null)}
        />
      )}
    </ToolPageShell>
  );
}

function AssetCard({ a, onPreview, onDownload, onEdit, onDelete }) {
  const Icon = KIND_ICON[a.kind] || FileText;
  return (
    <div data-testid={`marketing-card-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col hover:shadow-md transition">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${KIND_TINT[a.kind] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-600">{a.kind}{a.template_kind && ` · ${a.template_kind.replace(/_/g, " ")}`}</div>
            <h3 className="mt-0.5 font-display text-base font-semibold text-slate-900 leading-tight line-clamp-2">{a.title}</h3>
          </div>
        </div>
        <StatusPill status={a.status} />
      </div>
      <p className="mt-3 text-sm text-slate-600 line-clamp-3 flex-1">{a.description}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(a.program_categories || []).map((c) => (
          <span key={c} className={`text-[10px] capitalize rounded-full px-2 py-0.5 ${CATEGORY_TINT[c] || "bg-slate-100 text-slate-700"}`}>
            {c}
          </span>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onPreview}
          data-testid={`marketing-preview-${a.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1.5 hover:bg-slate-50 whitespace-nowrap"
        >
          <Eye className="w-3 h-3" /> Preview
        </button>
        <button
          type="button"
          onClick={onDownload}
          data-testid={`marketing-download-${a.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full px-2.5 py-1.5 whitespace-nowrap"
          title="Download the default-branded PDF (no clinic overrides)"
        >
          <Download className="w-3 h-3" /> PDF
        </button>
        {a.download_url && (
          <a
            href={a.download_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1.5 hover:bg-slate-50 whitespace-nowrap"
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button
          type="button"
          onClick={onEdit}
          data-testid={`marketing-edit-${a.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full px-2.5 py-1.5 whitespace-nowrap"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`marketing-delete-${a.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full px-2 py-1.5"
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
